import React from 'react';
import { Button } from '../ui/button';
import { ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import type { Project } from '@/data/projects';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

interface ReorderProjectsModalProps {
  isOpen: boolean;
  projects: Project[];
  language: 'hu' | 'en';
  onClose: () => void;
  onSave: (projectIds: string[]) => Promise<void>;
}

interface DraggableProjectItemProps {
  project: Project;
  index: number;
  language: 'hu' | 'en';
  moveProject: (dragIndex: number, hoverIndex: number) => void;
  moveUp: (index: number) => void;
  moveDown: (index: number) => void;
  isFirst: boolean;
  isLast: boolean;
}

const DraggableProjectItem: React.FC<DraggableProjectItemProps> = ({
  project,
  index,
  language,
  moveProject,
  moveUp,
  moveDown,
  isFirst,
  isLast
}) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'PROJECT',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'PROJECT',
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        moveProject(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <li
      ref={(node) => preview(drop(node))}
      className={`flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-opacity ${
        isDragging ? 'opacity-50' : 'opacity-100'
      }`}
    >
      <button
        ref={drag}
        className="cursor-move text-gray-400 hover:text-gray-600 p-1"
        title="Húzd ide a rendezéshez"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="text-sm font-medium text-gray-500 w-8">{index + 1}.</span>
      <span className="flex-1 text-sm">
        {language === 'hu' ? project.title.hu : project.title.en}
      </span>
      <div className="flex gap-1">
        <button
          onClick={() => moveUp(index)}
          disabled={isFirst}
          className={`p-1 rounded ${
            isFirst
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-600 hover:bg-gray-200'
          }`}
          title="Fel"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
        <button
          onClick={() => moveDown(index)}
          disabled={isLast}
          className={`p-1 rounded ${
            isLast
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-600 hover:bg-gray-200'
          }`}
          title="Le"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
};

export function ReorderProjectsModal({ 
  isOpen, 
  projects, 
  language, 
  onClose, 
  onSave 
}: ReorderProjectsModalProps) {
  const [reorderList, setReorderList] = React.useState<Project[]>([]);

  React.useEffect(() => {
    if (isOpen) {
      setReorderList([...projects]);
    }
  }, [isOpen, projects]);

  const moveProject = (dragIndex: number, hoverIndex: number) => {
    const newList = [...reorderList];
    const draggedItem = newList[dragIndex];
    newList.splice(dragIndex, 1);
    newList.splice(hoverIndex, 0, draggedItem);
    setReorderList(newList);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newList = [...reorderList];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setReorderList(newList);
  };

  const moveDown = (index: number) => {
    if (index === reorderList.length - 1) return;
    const newList = [...reorderList];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setReorderList(newList);
  };

  const handleSave = async () => {
    const sortedIds = reorderList.map(project => project.id);
    await onSave(sortedIds);
  };

  if (!isOpen) return null;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[80vh] flex flex-col">
          <h2 className="text-xl font-semibold mb-4">Projekt sorrend módosítása</h2>
          <p className="text-sm text-gray-600 mb-4">
            Húzd át a projekteket az <GripVertical className="inline h-4 w-4" /> ikon segítségével, vagy használd a fel/le gombokat. 
            A lista tetején lévő projekt jelenik meg először a weboldalon.
          </p>
          <div className="flex-1 overflow-y-auto border border-gray-300 rounded-md p-4 mb-4">
            <ul className="space-y-2">
              {reorderList.map((project, index) => (
                <DraggableProjectItem
                  key={project.id}
                  project={project}
                  index={index}
                  language={language}
                  moveProject={moveProject}
                  moveUp={moveUp}
                  moveDown={moveDown}
                  isFirst={index === 0}
                  isLast={index === reorderList.length - 1}
                />
              ))}
            </ul>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose} variant="outline">
              Mégse
            </Button>
            <Button onClick={handleSave}>
              Mentés
            </Button>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}